/**
 * The images written in the content of articles, posts and comments (rendered with v-html) point to the raw
 * Sonata files: a 150-250 KB JPEG/PNG for a phone, and both the desktop and the mobile copy of an article image
 * are downloaded (one of them is only hidden by CSS). This rewrites them to Cloudflare transformations:
 * - the same URL pattern as @nuxt/image (w=…,f=auto,q=80: same keys in the same order, so no duplicate
 *   transformation is billed, and the WAF rule on legacy patterns lets it through);
 * - a srcset in widths with a generous sizes: the browser picks by real pixel density, a retina phone gets the
 *   1200 px copy (never sharper before, never blurrier now). Cloudflare does not enlarge (scale-down), so a
 *   width above the original returns the original size;
 * - w=640 as src: the only transformation robots.txt allows to crawlers, so image search keeps the images;
 * - loading="lazy": the copy hidden by CSS is never downloaded.
 * GIFs (animations) and images outside /api/upload/media/ are left as they are.
 */
const MEDIA_SRC =
  /^((?:https:\/\/www\.weareplaystation\.fr)?)(\/api\/upload\/media\/([a-z_]+)\/[A-Za-z0-9_/.-]+\.(?:jpe?g|png|webp))$/i

// wysiwyg images are resized to 1200 px by Sonata: a larger width would bill a transformation of the same image
const widthsFor = (context: string) =>
  context === 'wysiwyg' ? [640, 1200] : [640, 1200, 1920]

const transformed = (origin: string, path: string, width: number) =>
  `${origin}/cdn-cgi/image/w=${width},f=auto,q=80${path}`

export const optimizeContentImages = (html?: string | null) => {
  if (!html || !html.includes('/api/upload/media/')) return html ?? ''

  return html.replace(/<img\b([^>]*?)\s*\/?>/gi, (img, attributes: string) => {
    const srcMatch = attributes.match(/\ssrc\s*=\s*"([^"]*)"/i)
    const media = srcMatch?.[1].trim().match(MEDIA_SRC)
    if (!srcMatch || !media || /\ssrcset\s*=/i.test(attributes)) return img

    const [, origin, path, context] = media
    const widths = widthsFor(context.toLowerCase())
    const srcset = widths
      .map((width) => `${transformed(origin, path, width)} ${width}w`)
      .join(', ')

    let rewritten = attributes.replace(
      srcMatch[0],
      ` src="${transformed(origin, path, 640)}" srcset="${srcset}" sizes="(max-width: 1200px) 100vw, 1200px"`,
    )
    if (!/\sloading\s*=/i.test(rewritten)) rewritten += ' loading="lazy"'
    if (!/\sdecoding\s*=/i.test(rewritten)) rewritten += ' decoding="async"'

    return `<img${rewritten}>`
  })
}
